import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { CURRENCY_MAP } from "@/lib/global";

const GlobalContext = createContext(null);
const DEVICE_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const guessCurrency = () => {
  const region = (navigator.language || "en-US").split("-")[1];
  const map = { US: "USD", GB: "GBP", CA: "CAD", AU: "AUD", JP: "JPY", IN: "INR", BR: "BRL", MX: "MXN", SG: "SGD", AE: "AED", ZA: "ZAR", CH: "CHF", SE: "SEK", NO: "NOK" };
  const eur = ["DE", "FR", "ES", "IT", "NL", "PT", "IE", "BE", "AT", "FI", "GR"];
  return map[region] || (eur.includes(region) ? "EUR" : "USD");
};

export const GlobalProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [currency, setCurrencyState] = useState(() => localStorage.getItem("lme_currency") || guessCurrency());
  const [tzPref, setTzPref] = useState(() => localStorage.getItem("lme_tz") || "auto");
  const [rates, setRates] = useState(null);

  useEffect(() => {
    api.get("/fx/rates").then(({ data }) => setRates(data.rates)).catch(() => setRates(null));
  }, []);

  const setCurrency = useCallback((c) => {
    localStorage.setItem("lme_currency", c);
    setCurrencyState(c);
  }, []);
  const setTimezone = useCallback((tz) => {
    localStorage.setItem("lme_tz", tz);
    setTzPref(tz);
  }, []);

  const timezone = tzPref === "auto" ? DEVICE_TZ : tzPref;
  const locale = i18n.resolvedLanguage || "en";

  const fmt = useCallback(
    (amount, code, decimals) =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency: code,
        maximumFractionDigits: decimals ?? (code === "JPY" ? 0 : 2),
        minimumFractionDigits: decimals ?? (code === "JPY" ? 0 : undefined),
      }).format(amount || 0),
    [locale]
  );

  const usd = useCallback((amount, decimals) => fmt(amount, "USD", decimals), [fmt]);

  const approx = useCallback(
    (amount, decimals) => {
      if (currency === "USD" || !rates?.[currency]) return null;
      return `≈ ${fmt((amount || 0) * rates[currency], currency, decimals)}`;
    },
    [currency, rates, fmt]
  );

  const fmtTime = useCallback(
    (iso, tz = timezone, opts = {}) => {
      if (!iso) return "";
      return new Intl.DateTimeFormat(locale, {
        weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
        timeZoneName: "short", timeZone: tz, ...opts,
      }).format(new Date(iso));
    },
    [locale, timezone]
  );

  const value = useMemo(
    () => ({
      language: locale, setLanguage: (l) => i18n.changeLanguage(l),
      currency, setCurrency, currencyMeta: CURRENCY_MAP[currency],
      tzPref, timezone, setTimezone, deviceTz: DEVICE_TZ,
      rates, usd, approx, fmtTime,
    }),
    [locale, i18n, currency, setCurrency, tzPref, timezone, setTimezone, rates, usd, approx, fmtTime]
  );
  return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
};

export const useGlobal = () => useContext(GlobalContext);
